//TODO: After mobile block, create a new componenent with all relevant hooks.
/* eslint-disable react-hooks/rules-of-hooks */
import "core-js/stable";
import "regenerator-runtime/runtime";
import React, { useEffect, useRef, useState } from "react";
import { GetServerSidePropsContext } from "next";
import useStateValue from "frontend/state/value";
import useAuthentication from "frontend/hooks/use-authentication";
import AppLoader from "frontend/loader/app-loader";
import Head from "next/head";
import tracking from "frontend/tracking";
import consts, { MIN_PAID_SEATS, Plan } from "shared/consts";
import getStripePaymentStatus, { stringToPlan, updateAccountSeats, openCustomerPortal } from "frontend/billingUtils";
import { deleteCookie, readCookieValue } from "frontend/utils/cookie-utils";
import { createBoard } from "frontend/services/boardsService";
import { useRouter } from "next/router";
import { BoardPermission, BoardState, isPublicPermission } from "shared/datamodel/schemas/board";
import mixpanel from "mixpanel-browser";
import Modal from "frontend/modal/modal";
import { Action } from "frontend/state/actions";
import { useSetAtom } from "jotai";
import { invoicePreviewsAtom, plansDataAtom } from "state-atoms/billing-atoms";
import { getPlansData, getUser, retrieveUpcomingInvoicePreview } from "frontend/api";
import { appLoadingAtom } from "state-atoms/general-atoms";
import OnboardingFlow from "../frontend/modals/onboarding";
import PaymentErrorModal from "frontend/billing/payment-error-modal";
import useShowOnboarding from "../frontend/hooks/use-onboarding";
import useUrlQueryParamValue from "frontend/hooks/use-url-query-param";
import BigModalContainer from "frontend/modals/big-modal-container";
import InviteView from "frontend/modals/onboarding/invite-view";
import { isBasicPlan, planToString } from "shared/util/billing";
import useSeatsCount from "frontend/hooks/use-seats-count";
import { ErrorBoundary } from "react-error-boundary";
import { ModalErrorPage } from "frontend/error/error-page";
import { PlanData } from "shared/datamodel/schemas";
import { useFetchWithJotai } from "frontend/hooks/use-fetch-with-jotai";
import _JSXStyle from "styled-jsx/style";
import { BoardsGridNew, GridError } from "frontend/boards-grid/boards-grid-new";
import useReloadUserAfterCheckout from "frontend/hooks/use-reload-user-after-checkout";
import { useStripeSetupIntentTracking } from "frontend/billing/use-stripe-setup-intent-tracking";
import { getIsMobile } from "utils/url-utils";
import { useFeatureFlagsState } from "frontend/hooks/use-feature-flag/state";
import { useLoadUserHasMondayBoards } from "frontend/canvas-page/hooks/use-show-monday-features";

if (typeof global !== "undefined") {
  Object.assign(global, { _JSXStyle });
}

export function getServerSideProps(context: GetServerSidePropsContext) {
  const { req, query } = context;
  const userAgent = req.headers["user-agent"] ?? "";
  const {
    payment_intent_client_secret = null,
    show_invite = null,
    redirect_status = null,
    setup_intent_client_secret = null,
    setup_intent = null,
  } = query;

  const isMobile = getIsMobile(userAgent);
  if (isMobile) {
    return { redirect: { destination: "/mobile", permanent: false } };
  }

  return {
    props: {
      paymentIntentClientSecret: payment_intent_client_secret,
      showInvite: show_invite,
      redirectStatus: redirect_status,
      setupIntentClientSecret: setup_intent_client_secret,
      setupIntentId: setup_intent,
    },
  };
}

export default function Home({
  paymentIntentClientSecret,
  showInvite,
  redirectStatus,
  setupIntentClientSecret,
  setupIntentId,
}: {
  paymentIntentClientSecret: string;
  showInvite: boolean;
  redirectStatus: string | null;
  setupIntentClientSecret: string | null;
  setupIntentId: string | null;
}) {
  //atoms
  const setInvoicePreviewsAtom = useSetAtom(invoicePreviewsAtom);
  const setAppLoading = useSetAtom(appLoadingAtom);

  const [showGridError, setShowGridError] = useState<GridError | null>(null);
  const [showPaymentFailureModal, setShowPaymentFailureModal] = useState<boolean>(false);
  const [showInviteModal, setShowInviteModal] = useState<boolean>(showInvite);

  const [{ user, boards }, dispatch] = useStateValue();
  const { isLoggedIn } = useAuthentication(true);

  const showOnboarding = useShowOnboarding();
  const { areAuthenticatedFeatureFlagsLoaded } = useFeatureFlagsState();
  const [error] = useUrlQueryParamValue("error");
  const [data] = useUrlQueryParamValue("data");

  const { userAccountCount, accountSubscribedCount, loadAccountSubscribedCount } = useSeatsCount(user);

  const router = useRouter();

  const didUpdateSeats = useRef(false); //bah react...
  const enablePaymentAbTest = useRef<boolean>(false); //bah react...

  useReloadUserAfterCheckout();
  useStripeSetupIntentTracking(setupIntentClientSecret, setupIntentId, redirectStatus);

  useFetchWithJotai<PlanData[]>({ key: "plansData", fetchFn: getPlansData, atom: plansDataAtom });
  useLoadUserHasMondayBoards();

  useEffect(() => {
    const enableAbTest = window && window.screen && window.screen.availWidth >= consts.MIN_WIDTH_TO_SHOW_PAYMENT_PAGE;
    enablePaymentAbTest.current = enableAbTest;
  }, []);

  useEffect(() => {
    error === "limitReached" && setShowGridError({ type: error, data });
  }, [error]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    async function checkPaymentStatus() {
      const status = await getStripePaymentStatus(paymentIntentClientSecret);
      tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "payment-status", status);
      if (status === "failure") {
        setShowPaymentFailureModal(true);
      }
    }

    if (paymentIntentClientSecret) {
      checkPaymentStatus();
    }
  }, [isLoggedIn]);

  async function updateSeats(seatsCount: number) {
    try {
      await updateAccountSeats(seatsCount);
      //reload users data after updating
      const user = await getUser();
      dispatch({ type: Action.UpdatedUser, payload: user });
    } catch (e) {
      console.error("couldn't update number of seats because of an error", e);
    }
  }

  useEffect(() => {
    if (!user) {
      return;
    }

    const isFreePlan = isBasicPlan(user?.planInfo?.plan_id);
    //Since we don't currently have a dedicated cron job that periodically checks the status of the users in the account (users added, accepted invite, removed etc.)
    //we check it here on page load. If we are on the free tier, make sure the account's subscription is synced with the amount of users in the account
    if (isFreePlan && !didUpdateSeats.current && userAccountCount !== 0) {
      didUpdateSeats.current = true;
      updateSeats(Math.max(userAccountCount, MIN_PAID_SEATS));
    } else if (user && !isFreePlan) {
      loadAccountSubscribedCount();
    }
    if (mixpanel) {
      const numberOfUsers = isFreePlan ? userAccountCount : accountSubscribedCount;
      mixpanel.register({ numberOfUsers: numberOfUsers });
    }
  }, [user, userAccountCount]);

  useEffect(() => {
    const isFreePlan = isBasicPlan(user?.planInfo?.plan_id);
    if (
      user?.planInfo &&
      !isFreePlan &&
      !didUpdateSeats.current &&
      accountSubscribedCount > user?.planInfo?.seats_count
    ) {
      didUpdateSeats.current = true;
      updateSeats(accountSubscribedCount);
    }
  }, [accountSubscribedCount]);

  useEffect(() => {
    async function createTemplateBoard(templateId: string) {
      try {
        const boardState = BoardState.templateNotSetUp;
        const name = "New Canvas";
        return await createBoard({
          name,
          state: boardState,
          templateIdOrAlias: templateId,
          projectId: null,
          teamId: null,
          dispatch,
        });
      } catch (e: any) {
        if (e?.response?.status === 406) {
          setShowGridError({ type: "limitReached", data: e.response.data.maxBoardsAllowed });
        } else {
          console.error("Error creating template board", e);
        }
        return null;
      }
    }

    async function initTemplate() {
      const templateId = readCookieValue(consts.COOKIE_NAMES.BOARD_TEMPLATE);

      if (templateId) {
        setAppLoading(true);
        const board = await createTemplateBoard(templateId);
        setAppLoading(false);
        deleteCookie(consts.COOKIE_NAMES.BOARD_TEMPLATE, "/", ".workcanvas.com");
        if (board) {
          router.push(`/d/${board.documentId}`);
        }
      } else {
        deleteCookie(consts.COOKIE_NAMES.BOARD_TEMPLATE, "/", ".workcanvas.com");
      }
    }

    //A workaround to detect hard-signups: If x minutes has passes since the user was created until he landed in the app, we consider it a new signup
    if (user && user.createdAt) {
      initTemplate();
    }
  }, [user]);

  useEffect(() => {
    if (boards && boards.length > 0) {
      const canvasesData = {
        totalCanvases: boards.length,
        totalOwnedCanvases: boards.filter((i) => i.isOwner).length,
        totalPublicCanvases: boards.filter((i) => isPublicPermission(i.permission)).length,
        totalPrivateCanvases: boards.filter((i) => i.permission === BoardPermission.private).length,
      };
      mixpanel.register(canvasesData);
    }
  }, [boards]);

  useEffect(() => {
    async function previewInvoices() {
      if (user?.planInfo?.seats_count) {
        const paidPlans = Object.values(Plan)
          .filter((i) => isNaN(parseInt(i as string)))
          .filter((i) => i != planToString(Plan.basic)?.toLowerCase());
        const invoicePromises: Promise<any>[] = [];
        for (const plan of paidPlans) {
          for (const period of ["month", "year"]) {
            const invoicePromise = retrieveUpcomingInvoicePreview(
              stringToPlan(plan as string)!,
              Math.max(user?.planInfo?.seats_count, MIN_PAID_SEATS),
              period as any
            );
            invoicePromises.push(invoicePromise);
          }
        }
        const invoices = await Promise.all(invoicePromises);
        setInvoicePreviewsAtom(invoices);
      }
    }
    if (isBasicPlan(user?.planInfo?.plan_id)) {
      previewInvoices();
    }
  }, [user?.planInfo?.seats_count]);

  function renderBoardsState() {
    return (
      <React.Fragment>
        {!showOnboarding && !showInviteModal && <BoardsGridNew showGridError={showGridError} />}
        {!showOnboarding && showInviteModal && (
          <BigModalContainer source="invite-view">
            <InviteView onCompleted={() => setShowInviteModal(false)} />
          </BigModalContainer>
        )}
        {showOnboarding && <OnboardingFlow />}
      </React.Fragment>
    );
  }

  function render() {
    if (isLoggedIn && areAuthenticatedFeatureFlagsLoaded) {
      return renderBoardsState();
    }
    return <AppLoader />;
  }

  return (
    <ErrorBoundary FallbackComponent={ModalErrorPage}>
      <div>
        <Head>
          <title>WorkCanvas.com</title>
        </Head>
        {render()}
        {showPaymentFailureModal && (
          <Modal dimBackground={true}>
            <PaymentErrorModal
              onClick={() => openCustomerPortal({ type: "update-payment" })}
              onDismiss={() => setShowPaymentFailureModal(false)}
            />
          </Modal>
        )}
      </div>
    </ErrorBoundary>
  );
}
